import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';
import { fetchDashboard } from './api';
import { dashboardFilterAtom } from './filterAtom';
import { dashboardAtom } from './atoms';
import { ServerDashboardDashboard } from './dashboardType';

export const useDashboardData = () => {
  const filters = useAtomValue(dashboardFilterAtom);
  const setDashboardData = useSetAtom(dashboardAtom);
  const [pageMultiplier, setPageMultiplier] = useState(1);
  const [previousMultiplier, setPreviousMultiplier] = useState(1);

  const {
    data: dashboardData,
    isLoading: isInitialLoading,
    isError,
    error,
    refetch,
    isFetching,
    isRefetching,
  } = useQuery<ServerDashboardDashboard>({
    placeholderData: (previousData) =>
      pageMultiplier > 1 ? previousData : undefined,
    queryFn: () =>
      fetchDashboard({
        ...filters,
        activePage: 1,
        itemsPerPage: filters.itemsPerPage * pageMultiplier,
      }),
    queryKey: ['dashboard', filters, pageMultiplier],
    refetchInterval: (q) => (q.state.status === 'error' ? false : 3000),
    refetchOnWindowFocus: false,
  });

  const totalJobs = dashboardData?.total || 0;
  const currentItemsPerPage = filters.itemsPerPage * pageMultiplier;
  const hasNextPage = currentItemsPerPage < totalJobs;

  // isLoading: true on initial load OR when filters change (pageMultiplier=1 and fetching)
  const isLoadingInitialData =
    pageMultiplier === 1 && isFetching && !isRefetching;
  const isLoading = isInitialLoading || isLoadingInitialData;

  // isFetchingNextPage: true when pageMultiplier increased and we're fetching (not during auto-refetch)
  const isLoadingMoreData = pageMultiplier > previousMultiplier;
  const isFetchingNextPage =
    (isFetching && !isRefetching && isLoadingMoreData) || isLoadingMoreData;

  // Update previousMultiplier when data successfully loads
  useEffect(() => {
    if (dashboardData && !isFetching) {
      setPreviousMultiplier(pageMultiplier);
    }
  }, [dashboardData, isFetching, pageMultiplier]);

  const fetchNextPage = () => {
    if (hasNextPage) {
      setPageMultiplier((previous) => previous + 1);
    }
  };

  // Reset multiplier when filters change
  useEffect(() => {
    setPageMultiplier(1);
    setPreviousMultiplier(1);
  }, [filters]);

  useEffect(() => {
    if (dashboardData) {
      setDashboardData(dashboardData);
    }
  }, [dashboardData, setDashboardData]);

  return {
    dashboardData,
    error,
    fetchNextPage,
    hasNextPage,
    isError,
    isFetchingNextPage,
    isLoading,
    refetch,
  };
};
